import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ShoppingCart, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/shop/ShopContext";

const ProductModal = ({ product, open, onClose }) => {
  const { addToCart, config } = useShop();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open, product?.id]);

  if (!product) return null;
  const images = product.images?.length ? product.images : [product.image];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl overflow-hidden border-white/10 bg-[#14171D] p-0 text-neutral-100" data-testid="product-modal">
        <div className="grid md:grid-cols-2">
          <div className="relative bg-[#0C0E12]">
            <img
              src={images[idx]}
              alt={`${product.name} — foto ${idx + 1}`}
              className="aspect-square h-full w-full object-cover"
              data-testid="product-gallery-main"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((idx - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-[#FF5500]"
                  data-testid="gallery-prev"
                  aria-label="Foto precedente"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIdx((idx + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-[#FF5500]"
                  data-testid="gallery-next"
                  aria-label="Foto successiva"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-3 py-1 font-mono text-[11px] text-neutral-200" data-testid="gallery-counter">
                  {idx + 1} / {images.length}
                </span>
              </>
            )}
          </div>
          <div className="flex flex-col p-6 sm:p-8">
            <span className="inline-block w-fit rounded border border-[#FF5500]/30 bg-black/50 px-2 py-0.5 font-mono text-[11px] text-[#FF5500]">
              {product.code}
            </span>
            <h2 className="mt-3 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-neutral-100">
              {product.name}
            </h2>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {product.vehicles.map((v) => (
                <span key={v} className="rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-neutral-300">
                  {v}
                </span>
              ))}
            </div>
            {product.description && <p className="mt-4 text-sm leading-relaxed text-neutral-400">{product.description}</p>}

            {images.length > 1 && (
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border transition-colors ${
                      i === idx ? "border-[#FF5500]" : "border-white/15 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`miniatura ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-auto pt-6">
              <div className="flex items-end justify-between">
                <span className="font-display text-4xl text-neutral-50">€ {product.price.toFixed(2)}</span>
                <span className="text-xs text-neutral-500">Giacenza: {product.stock}</span>
              </div>
              <button
                onClick={() => {
                  addToCart(product);
                  toast.success("Aggiunto al carrello", { description: product.name });
                }}
                data-testid="modal-add-to-cart-btn"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF5500] py-3.5 font-display text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
              >
                <ShoppingCart className="h-4 w-4" /> Aggiungi al carrello
              </button>
              <a
                href={config.phone_href}
                data-testid="modal-phone-link"
                className="mt-3 flex items-center justify-center gap-2 text-xs text-neutral-400 transition-colors hover:text-[#FF5500]"
              >
                <Phone className="h-3.5 w-3.5" /> Dubbi sulla compatibilità? Chiama {config.phone}
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
